import classNames from 'classnames';
import {ComponentProps, MouseEvent, PropsWithChildren, ReactNode, useCallback, useRef} from 'react';

import Col from '../../../common/Col';
import Row from '../../../common/Row';
import GameAudio, {GameAudioHandle} from '../GameAudio';

type Props = Pick<
  ComponentProps<'button'>,
  'id' | 'className' | 'disabled' | 'onClick' | 'onMouseDown' | 'style' | 'tabIndex' | 'aria-label'
> & {
  left?: ReactNode;
  right?: ReactNode;
  light?: boolean;
  hoverDisabled?: boolean;
  isToggled?: boolean;
};

const NormalButton = ({
  children,
  left,
  right,
  light,
  hoverDisabled,
  isToggled,
  onMouseDown,
  className,
  ...rest
}: PropsWithChildren<Props>) => {
  const audioRef = useRef<GameAudioHandle>(null);
  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      audioRef.current?.play();
      onMouseDown?.(e);
    },
    [onMouseDown]
  );

  return (
    <button
      className={classNames(
        'border-normal font-sans rounded-2xl p-xs min-w-[3rem] text-xl disabled:bg-white disabled:text-primary-700 disabled:shadow-solid-light-gray disabled:border-gray-50 disabled:text-inherit disabled:pointer-events-none active:shadow-none active:translate-y-2 active:-translate-x-2 transition duration-100',
        'mobile:text-base',
        light
          ? classNames(
              'border-gray-100 shadow-solid-light-green',
              !hoverDisabled && 'hover:bg-primary-700 hover:text-white',
              isToggled ? 'bg-primary-700 text-white' : 'bg-white text-primary-700'
            )
          : classNames(
              'border-primary-300 text-white shadow-solid-green',
              !hoverDisabled && 'hover:bg-primary-600',
              isToggled ? 'bg-primary-600' : 'bg-primary-700',
              'focus-visible:bg-primary-600'
            ),
        className
      )}
      onMouseDown={handleMouseDown}
      {...rest}
    >
      <GameAudio ref={audioRef} src="sounds/click.mp3" />
      <Row className="space-x-5 justify-between items-center text-center leading-none">
        {children && !left && right && <Col className="w-3" />}
        {left && <Col>{left}</Col>}
        {children && <Col className="grow">{children}</Col>}
        {right && <Col>{right}</Col>}
        {children && left && !right && <Col className="w-3" />}
      </Row>
    </button>
  );
};

export default NormalButton;
